import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastError, displayToastSuccess } from '@/helpers/display-toast';
import { UpsertClientsBulkRequest } from '@/modules/onboarding/clients-setup/types/request/upsert-client-bulk.request';
import {
  invalidateOnboardingClientValidationReport,
  invalidateOnboardingProjectQueries,
} from '@/modules/onboarding/common/utils/onboarding-query-invalidation';

import { upsertClientsBulk } from '../api/clients';

export function useUpsertClients({ onSuccess }: { onSuccess?: (addedCount: number) => void } = {}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpsertClientsBulkRequest) => upsertClientsBulk(data),
    onSuccess: async (_, variables) => {
      const members = variables.items;

      if (members.length === 1) {
        displayToastSuccess(
          t('authenticated.onboarding.clients:addModal.clientAdded', { name: members[0].name })
        );
      } else {
        displayToastSuccess(t('authenticated.onboarding.clients:addModal.clientsAdded'));
      }

      await invalidateOnboardingClientValidationReport(queryClient);
      await invalidateOnboardingProjectQueries(queryClient);
      onSuccess?.(members.length);
    },
    onError: (error: Error) => {
      displayToastError(error.message);
    },
  });
}
